import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./Button";
import {
  TRACKING_DAMPING,
  TRACKING_JITTER_STRENGTH,
  TRACKING_SPEED_SCALE,
} from "../tracking/TrackingTaskStage";
import { dollars } from "../lossFrameMoney";

const ARENA_WIDTH = 680;
const ARENA_HEIGHT = 360;
const BASE_RADIUS = 14.4;
const CLIP_DURATION_MS = 4000;
const FRAME_MS = 1000 / 60;
const FIXED_RANDOM_SEED = 20260226;
const SMIRKING_FACE = "\u{1F60F}";
const TIRED_FACE = "\u{1F62B}";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function finiteNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function buildClipPositions(radius, jitterStrength) {
  const random = seededRandom(FIXED_RANDOM_SEED);
  const positions = [];
  const totalFrames = Math.floor(CLIP_DURATION_MS / FRAME_MS);

  let x = ARENA_WIDTH / 2;
  let y = ARENA_HEIGHT / 2;
  let vx = 0;
  let vy = 0;

  positions.push({ x, y });

  for (let frame = 0; frame < totalFrames; frame += 1) {
    vx = vx * TRACKING_DAMPING + (random() * 2 - 1) * jitterStrength;
    vy = vy * TRACKING_DAMPING + (random() * 2 - 1) * jitterStrength;

    x += vx * (FRAME_MS / 16.67) * TRACKING_SPEED_SCALE;
    y += vy * (FRAME_MS / 16.67) * TRACKING_SPEED_SCALE;

    const minX = radius;
    const maxX = ARENA_WIDTH - radius;
    const minY = radius;
    const maxY = ARENA_HEIGHT - radius;

    if (x < minX || x > maxX) {
      vx *= -1;
      x = clamp(x, minX, maxX);
    }

    if (y < minY || y > maxY) {
      vy *= -1;
      y = clamp(y, minY, maxY);
    }

    positions.push({ x, y });
  }

  return positions;
}

export function TrackingClipExample({
  radiusMultiplier = 1,
  jitterStrength = TRACKING_JITTER_STRENGTH,
  dollarsPerSecond = 0,
  startingMoneyDollars = null,
  emojiMode = false,
  lossFrame = false,
  onClipPlayed,
}) {
  const safeRadiusMultiplier = Number.isFinite(Number(radiusMultiplier))
    ? Number(radiusMultiplier)
    : 1;
  const radius = BASE_RADIUS * safeRadiusMultiplier;

  const positions = useMemo(
    () => buildClipPositions(radius, jitterStrength),
    [radius, jitterStrength]
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const [frameIndex, setFrameIndex] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [insideSeconds, setInsideSeconds] = useState(0);
  const [isCursorInside, setIsCursorInside] = useState(false);
  const [playRunId, setPlayRunId] = useState(0);
  const arenaRef = useRef(null);
  const cursorPositionRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }

    let rafId;
    const startTime = performance.now();
    let previousNow = startTime;
    let accumulatedInsideMs = 0;

    const tick = (now) => {
      const deltaMs = now - previousNow;
      previousNow = now;
      const elapsed = now - startTime;
      const nextFrameIndex = Math.min(
        positions.length - 1,
        Math.floor(elapsed / FRAME_MS)
      );
      setFrameIndex(nextFrameIndex);
      setElapsedMs(Math.min(elapsed, CLIP_DURATION_MS));

      const nextPosition = positions[nextFrameIndex];
      const cursorPosition = cursorPositionRef.current;
      const hasCursor = cursorPosition && Number.isFinite(cursorPosition.x) && Number.isFinite(cursorPosition.y);
      let insideThisFrame = false;
      if (nextPosition && hasCursor) {
        const dx = cursorPosition.x - nextPosition.x;
        const dy = cursorPosition.y - nextPosition.y;
        const distance = Math.hypot(dx, dy);
        if (distance <= radius) {
          insideThisFrame = true;
          accumulatedInsideMs += deltaMs;
          setInsideSeconds(accumulatedInsideMs / 1000);
        }
      }
      setIsCursorInside(insideThisFrame);

      if (elapsed >= CLIP_DURATION_MS) {
        setIsPlaying(false);
        setIsCursorInside(false);
        return;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isPlaying, positions, radius, playRunId]);

  const circlePosition = positions[frameIndex] || positions[0] || { x: ARENA_WIDTH / 2, y: ARENA_HEIGHT / 2 };

  function handleArenaMouseMove(event) {
    const bounds = arenaRef.current?.getBoundingClientRect();
    if (!bounds) {
      return;
    }

    cursorPositionRef.current = {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
  }

  function handleArenaMouseLeave() {
    cursorPositionRef.current = null;
    setIsCursorInside(false);
  }

  function handlePlayClip() {
    onClipPlayed?.();
    setFrameIndex(0);
    setElapsedMs(0);
    setInsideSeconds(0);
    setIsCursorInside(false);
    setIsPlaying(true);
    setPlayRunId((current) => current + 1);
  }

  const dollarsPerSecondNumber = Math.max(0, finiteNumber(dollarsPerSecond));
  const startingMoney = Math.max(
    0,
    finiteNumber(
      startingMoneyDollars,
      CLIP_DURATION_MS / 1000 * dollarsPerSecondNumber
    )
  );
  const moneyEarned = insideSeconds * dollarsPerSecondNumber;
  const moneyLost = Math.max(0, elapsedMs / 1000 - insideSeconds) * dollarsPerSecondNumber;
  const moneyLeft = clamp(startingMoney - moneyLost, 0, startingMoney);
  const isMoneyBeingLost = lossFrame && isPlaying && !isCursorInside;
  const moneyValueClass = isMoneyBeingLost ? "text-red-600" : "text-gray-900";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Button type="button" handleClick={handlePlayClip}>
          {isPlaying ? "Playing example..." : "Play example"}
        </Button>
        <span className="text-2xl text-gray-1200">
          Time inside circle: <b>{insideSeconds.toFixed(2)}</b>s{" "}
          {lossFrame ? (
            <>
              (money left: <b className={moneyValueClass}>{dollars(moneyLeft)}</b>)
            </>
          ) : (
            <>
              (money earned: <b>{dollars(moneyEarned)}</b>)
            </>
          )}
        </span>
      </div>
      <div className="mx-auto w-full overflow-x-auto">
        <div
          ref={arenaRef}
          className="relative mx-auto rounded-md border border-gray-300 bg-gray-50 overflow-hidden"
          onMouseMove={handleArenaMouseMove}
          onMouseLeave={handleArenaMouseLeave}
          style={{ width: `${ARENA_WIDTH}px`, height: `${ARENA_HEIGHT}px`, minWidth: `${ARENA_WIDTH}px` }}
        >
          <div
            className={
              emojiMode
                ? "group absolute flex items-center justify-center rounded-full cursor-default select-none"
                : "absolute rounded-full border border-gray-700 bg-gray-500/80 transition-colors hover:bg-gray-400/80"
            }
            style={{
              width: `${radius * 2}px`,
              height: `${radius * 2}px`,
              left: `${circlePosition.x - radius}px`,
              top: `${circlePosition.y - radius}px`,
              fontSize: `${radius * 1.65}px`,
              lineHeight: 1,
            }}
          >
            {emojiMode ? (
              <>
                <span className="pointer-events-none group-hover:hidden" aria-label="Smirking face">
                  {SMIRKING_FACE}
                </span>
                <span className="pointer-events-none hidden group-hover:inline" aria-label="Tired face">
                  {TIRED_FACE}
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
